import { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { Field, Label, Select } from '@shared/ui/Input';
import { useLazyDiffContractVersionsQuery } from '@shared/api';
import { AnalysisBar } from '@shared/ui/AnalysisBar';
import type { ContractVersion } from '@entities/legal/types';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Controls = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: end;
`;

const Summary = styled.div`
  background: ${({ theme }) => theme.colors.primarySoft};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;
`;

const ItemCard = styled.div<{ $tone: 'good' | 'bad' | 'neutral' }>`
  border-left: 3px solid
    ${({ $tone, theme }) =>
      $tone === 'good'
        ? theme.colors.status.success
        : $tone === 'bad'
          ? theme.colors.status.danger
          : theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(3.5)};
  border-radius: ${({ theme }) => theme.radius.md};
  animation: ${keyframes`
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  `} 240ms ${({ theme }) => theme.motion.easeOut} both;
`;

const ItemHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2.5)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Clause = styled.span`
  font-weight: 700;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ChangeSummary = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing(2.5)};
  line-height: 1.5;
`;

const Pair = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(2.5)};
  font-size: 12px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const PaneTitle = styled.div`
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: 4px;
`;

const Pane = styled.div<{ $tone: 'from' | 'to' }>`
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.radius.sm};
  border-top: 2px solid
    ${({ theme, $tone }) => ($tone === 'from' ? theme.colors.text.muted : theme.colors.primary)};
  white-space: pre-wrap;
  max-height: 180px;
  overflow: auto;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Note = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-style: italic;
`;

const Empty = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  padding: ${({ theme }) => theme.spacing(4)};
  text-align: center;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const Counter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Pill = styled.span<{ $tone: 'good' | 'bad' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  background: ${({ $tone, theme }) =>
    $tone === 'good'
      ? theme.colors.status.success + '22'
      : $tone === 'bad'
        ? theme.colors.status.danger + '22'
        : theme.colors.surface};
  color: ${({ $tone, theme }) =>
    $tone === 'good'
      ? theme.colors.status.success
      : $tone === 'bad'
        ? theme.colors.status.danger
        : theme.colors.text.secondary};
`;

const CHANGE_LABELS: Record<string, string> = {
  added: 'Добавлен',
  removed: 'Удалён',
  modified: 'Изменён',
};

function favorableTone(value: boolean | null): 'good' | 'bad' | 'neutral' {
  if (value === true) return 'good';
  if (value === false) return 'bad';
  return 'neutral';
}

function favorableLabel(value: boolean | null): string {
  if (value === true) return 'В нашу пользу';
  if (value === false) return 'Не в нашу пользу';
  return 'Нейтрально';
}

interface Props {
  contractId: string;
  versions: ContractVersion[];
}

export function VersionDiff({ contractId, versions }: Props) {
  // Версии в карточке отсортированы по version_no DESC. Для diff удобнее:
  // from = предыдущая, to = последняя.
  const sorted = [...versions].sort((a, b) => a.version_no - b.version_no);
  const initialFrom = sorted[sorted.length - 2]?.id ?? sorted[0]?.id ?? '';
  const initialTo = sorted[sorted.length - 1]?.id ?? '';
  const [fromId, setFromId] = useState<string>(initialFrom);
  const [toId, setToId] = useState<string>(initialTo);
  const [trigger, { data, isFetching, error }] = useLazyDiffContractVersionsQuery();

  const canCompare = !!fromId && !!toId && fromId !== toId;

  function run() {
    if (!canCompare) return;
    trigger({ contractId, from: fromId, to: toId });
  }

  if (versions.length < 2) {
    return (
      <Empty>
        Чтобы сравнить редакции, нужно как минимум две версии договора. Загрузите новую редакцию (от
        контрагента или нашу встречную) — кнопка «Загрузить новую версию» выше.
      </Empty>
    );
  }

  const goodCount = data?.items.filter((i) => i.favorable_for_satkom === true).length ?? 0;
  const badCount = data?.items.filter((i) => i.favorable_for_satkom === false).length ?? 0;
  const neutralCount = (data?.items.length ?? 0) - goodCount - badCount;

  return (
    <Wrap>
      <Controls>
        <Field>
          <Label htmlFor="diff-from">Откуда (старая редакция)</Label>
          <Select id="diff-from" value={fromId} onChange={(e) => setFromId(e.target.value)}>
            {sorted.map((v) => (
              <option key={v.id} value={v.id}>
                v{v.version_no} · {v.source === 'counterparty' ? 'от контрагента' : 'наша'}
              </option>
            ))}
          </Select>
        </Field>
        <Field>
          <Label htmlFor="diff-to">Куда (новая редакция)</Label>
          <Select id="diff-to" value={toId} onChange={(e) => setToId(e.target.value)}>
            {sorted.map((v) => (
              <option key={v.id} value={v.id}>
                v{v.version_no} · {v.source === 'counterparty' ? 'от контрагента' : 'наша'}
              </option>
            ))}
          </Select>
        </Field>
        <Button type="button" onClick={run} disabled={!canCompare || isFetching}>
          {isFetching ? 'Сравниваем…' : 'Сравнить'}
        </Button>
      </Controls>

      {fromId === toId && <Note>Выберите две разные версии.</Note>}

      {isFetching && (
        <AnalysisBar
          title="LLM сопоставляет редакции…"
          hint="Сначала разбиваем оба текста на пункты по нумерации, потом одним запросом отдаём изменения модели."
        />
      )}

      {error && !isFetching && (
        <Empty>
          Не удалось получить сравнение. Возможно, у одной из версий нет распознаваемого текста.
          Перезапустите анализ или загрузите версию заново.
        </Empty>
      )}

      {data && !isFetching && (
        <>
          <Summary>
            <strong>
              v{data.from_version_no} → v{data.to_version_no}.
            </strong>{' '}
            {data.summary ?? 'Сравнение завершено. Подробности по каждому изменению ниже.'}
          </Summary>
          <Counter>
            <span>
              Всего изменений: <strong>{data.items.length}</strong>
            </span>
            <Pill $tone="good">✓ В нашу пользу: {goodCount}</Pill>
            <Pill $tone="bad">✗ Против нас: {badCount}</Pill>
            <Pill $tone="neutral">≈ Нейтрально: {neutralCount}</Pill>
          </Counter>

          {data.items.length === 0 && (
            <Empty>
              Между этими версиями не нашлось содержательных изменений. Возможно, отличается только
              форматирование.
            </Empty>
          )}

          {data.items.map((it) => {
            const tone = favorableTone(it.favorable_for_satkom);
            return (
              <ItemCard key={`${it.clause}-${it.change_type}`} $tone={tone}>
                <ItemHeader>
                  <Clause>Пункт {it.clause}</Clause>
                  <Badge>{CHANGE_LABELS[it.change_type] ?? it.change_type}</Badge>
                  {it.favorable_for_satkom !== null && (
                    <Pill $tone={tone}>{favorableLabel(it.favorable_for_satkom)}</Pill>
                  )}
                </ItemHeader>
                {it.summary && <ChangeSummary>{it.summary}</ChangeSummary>}
                <Pair>
                  <div>
                    <PaneTitle>v{data.from_version_no} (старая)</PaneTitle>
                    <Pane $tone="from">{it.from_text ?? '— пункт отсутствует —'}</Pane>
                  </div>
                  <div>
                    <PaneTitle>v{data.to_version_no} (новая)</PaneTitle>
                    <Pane $tone="to">{it.to_text ?? '— пункт удалён —'}</Pane>
                  </div>
                </Pair>
              </ItemCard>
            );
          })}

          {data.analyzed_clauses < data.items.length && (
            <Note>
              Проанализированы LLM: {data.analyzed_clauses} из {data.items.length}. Остальные
              изменения показаны без LLM-оценки — слишком много правок в одном проходе.
            </Note>
          )}
        </>
      )}
    </Wrap>
  );
}
